import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { getConsent, subscribeConsent } from "@/lib/consent";

const rawPublisher = (process.env.REACT_APP_ADSENSE_PUBLISHER_ID || "").trim();
const publisherId = rawPublisher
  ? rawPublisher.startsWith("ca-")
    ? rawPublisher
    : `ca-${rawPublisher}`
  : "";

let scriptPromise = null;

function loadAdSenseScript() {
  if (document.querySelector('script[data-adsense-script="true"]')) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.adsenseScript = "true";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(publisherId)}`;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("No se pudo cargar AdSense"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function QuietSlot({ minHeight, message }) {
  return (
    <div
      data-testid="ad-slot-editorial-banner"
      aria-label="Espacio publicitario"
      className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 text-center"
      style={{ minHeight }}
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Publicidad</span>
      <span className="text-xs text-ink-muted/70">{message}</span>
    </div>
  );
}

export function AdSlot({ slot = "0000000000", format = "auto", minHeight = 250, label = "Publicidad", className = "" }) {
  const consent = useSyncExternalStore(subscribeConsent, getConsent);
  const accepted = consent?.decision === "accepted";
  const hostRef = useRef(null);
  const pushedRef = useRef(false);
  const [nearViewport, setNearViewport] = useState(false);

  useEffect(() => {
    if (!hostRef.current || !publisherId || !accepted) return;
    const node = hostRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [accepted]);

  useEffect(() => {
    if (!publisherId || !accepted || !nearViewport || pushedRef.current) return;
    let cancelled = false;
    loadAdSenseScript()
      .then(() => {
        if (cancelled || !hostRef.current || pushedRef.current) return;
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
        pushedRef.current = true;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [nearViewport, accepted]);

  if (!publisherId) {
    return (
      <div
        data-testid="ad-slot-editorial-banner"
        aria-label="Espacio publicitario"
        className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 text-center ${className}`}
        style={{ minHeight }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">{label}</span>
        <span className="font-serif text-lg italic text-ink-muted/70">Tu anuncio podría estar aquí</span>
        <span className="text-xs text-ink-muted/60">Google AdSense · pendiente de tu ID de editor</span>
      </div>
    );
  }

  if (!consent) {
    return <QuietSlot minHeight={minHeight} message="Esperando tu decisión sobre las cookies" />;
  }

  if (!accepted) {
    return <QuietSlot minHeight={minHeight} message="Desactivada en tu navegador: no se cargan cookies publicitarias" />;
  }

  return (
    <div ref={hostRef} data-testid="ad-slot-editorial-banner" className={`w-full overflow-hidden ${className}`}>
      <div className="mb-2 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">{label}</div>
      <ins
        className="adsbygoogle block w-full"
        style={{ display: "block", minHeight }}
        data-ad-client={publisherId}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
